"use client";

import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useEffect, type ReactNode } from "react";
import { cn } from "@routecore/core";

export function Modal({
  open,
  onClose,
  children,
  panelClassName,
  zIndex = 80,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  panelClassName?: string;
  zIndex?: number;
}) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <div
          className="fixed inset-0 grid place-items-center overflow-y-auto p-4"
          style={{ zIndex }}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            className="fixed inset-0 bg-ink/45 backdrop-blur-[3px] nad-modal-overlay"
          />
          <motion.div
            initial={{ opacity: 0, y: 26, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ type: "spring", bounce: 0.22, duration: 0.55 }}
            className={cn(
              "relative my-auto w-full max-w-md rounded-3xl border border-line bg-porcelain p-6 shadow-lifted nad-modal-content",
              panelClassName,
            )}
          >
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export function ModalHeader({
  eyebrow,
  title,
  onClose,
}: {
  eyebrow: string;
  title: string;
  onClose: () => void;
}) {
  return (
    <div className="mb-5 flex items-start justify-between gap-4">
      <div>
        <div className="text-[10px] font-bold tracking-[0.22em] text-accent uppercase">{eyebrow}</div>
        <h2 className="mt-1 font-display text-2xl font-semibold tracking-tight">{title}</h2>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="grid size-9 shrink-0 place-items-center rounded-full border border-line text-ink-soft transition-colors hover:bg-cream hover:text-ink"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}
